import { useEffect, useState } from 'react';
import { fetchBlobUrl } from '../lib/api';
import { documents } from '../lib/endpoints';
import { EmptyState, ErrorState, Skeleton } from './Feedback';

interface PreviewState {
  url: string | null;
  contentType: string;
  error: string | null;
  loading: boolean;
}

/**
 * Renders the source document for a gateway document id.
 *
 * The file endpoint is bearer-protected and an <iframe>/<img> cannot carry an Authorization
 * header, so the bytes are fetched here and handed to the element as an object URL.
 */
export function DocumentPreview({
  documentId,
  fileName,
  className = '',
}: {
  documentId: string | null;
  fileName?: string | null;
  className?: string;
}): JSX.Element {
  const [state, setState] = useState<PreviewState>({
    url: null,
    contentType: '',
    error: null,
    loading: false,
  });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!documentId) {
      setState({ url: null, contentType: '', error: null, loading: false });
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    let active = true;

    setState({ url: null, contentType: '', error: null, loading: true });

    fetchBlobUrl(documents.filePath(documentId), controller.signal)
      .then((result) => {
        objectUrl = result.url;
        if (!active) {
          URL.revokeObjectURL(result.url);
          return;
        }
        setState({ url: result.url, contentType: result.contentType, error: null, loading: false });
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setState({
          url: null,
          contentType: '',
          error: cause instanceof Error ? cause.message : 'The document could not be loaded.',
          loading: false,
        });
      });

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId, reloadToken]);

  if (!documentId) {
    return (
      <div className={`preview ${className}`.trim()}>
        <EmptyState title="No document selected" description="Pick a row to see the source file." icon="🗎" />
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className={`preview ${className}`.trim()}>
        <div className="preview-loading">
          <Skeleton height="100%" radius={8} />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`preview ${className}`.trim()}>
        <ErrorState
          message="Preview unavailable"
          detail={state.error}
          onRetry={() => setReloadToken((token) => token + 1)}
        />
      </div>
    );
  }

  const isImage = state.contentType.startsWith('image/');
  const isPdf = state.contentType.includes('pdf');
  const label = fileName ?? 'Document preview';

  // Chrome's built-in PDF viewer opens its own thumbnail sidebar by default, which in a narrow
  // preview column reads as a second, smaller copy of the same page sitting next to the real one.
  // navpanes=0 is one of the viewer's own open parameters and keeps it closed until asked for.
  const pdfSrc = state.url ? `${state.url}#navpanes=0` : null;

  return (
    <div className={`preview ${className}`.trim()}>
      <div className="preview-toolbar">
        <span className="preview-name" title={label}>
          {label}
        </span>
        <span className="preview-tools">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            Reload
          </button>
          {state.url ? (
            <a className="btn btn-ghost btn-sm" href={state.url} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : null}
          {state.url ? (
            <a className="btn btn-ghost btn-sm" href={state.url} download={fileName ?? 'document'}>
              Download
            </a>
          ) : null}
        </span>
      </div>
      <div className="preview-surface">
        {state.url && isImage ? (
          <img className="preview-image" src={state.url} alt={label} />
        ) : state.url && isPdf ? (
          <iframe className="preview-frame" src={pdfSrc ?? state.url} title={label} />
        ) : state.url ? (
          <EmptyState
            title="Preview not supported"
            description={`This file is ${state.contentType || 'of an unknown type'}. Use Download to open it locally.`}
            icon="🗎"
          />
        ) : null}
      </div>
    </div>
  );
}
